import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  AppBar,
  Toolbar,
  Typography,
  Modal,
  Snackbar,
  Alert,
  Container,
  InputAdornment,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";

const StreamPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredStreams, setFilteredStreams] = useState([]);
  const [streams, setStreams] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [streamToDelete, setStreamToDelete] = useState(null);
  const [streamNameToDelete, setStreamNameToDelete] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [permissions, setPermissions] = useState({
    uRead: 0,
    uWrite: 0,
    uModify: 0,
    uDelete: 0,
    isPermission: 0,
  });
  const [formId, setFormId] = useState(null);
  const [loading, setLoading] = useState(true); // For permission loading
  const [streamLoading, setStreamLoading] = useState(false); // For stream data loading
  const [error, setError] = useState(null);

  const token = sessionStorage.getItem("token");
  const apiUrl = process.env.REACT_APP_BASE_URL;

  // Fetch Form ID
  const fetchFormId = async () => {
    try {
      const response = await fetch(
        "https://wsms.aidigitalsec.com/api/ExclusivePermission/Form_Authorizer",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ moduleId: 7 }),
        }
      );
      const data = await response.json();
      console.log("Form Authorizer Response:", data); // Debug log
      if (response.ok) {
        const streamForm = data.find((form) => form.formName === "Stream");
        if (streamForm) {
          setFormId(streamForm.formId);
          console.log("Form ID set:", streamForm.formId); // Debug log
        } else {
          setError("Stream form not found in response");
          setLoading(false);
        }
      } else {
        setError("Failed to fetch form ID");
        setSnackbar({
          open: true,
          message: "Failed to fetch form ID",
          severity: "error",
        });
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching form ID:", error); // Debug log
      setError("Error fetching form ID");
      setSnackbar({
        open: true,
        message: "Error fetching form ID",
        severity: "error",
      });
      setLoading(false);
    }
  };

  // Fetch Permissions
  const fetchPermissions = async () => {
    if (!formId) return;
    try {
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ formId }),
      });
      const data = await response.json();
      console.log("Module Authorizer Response:", data); // Debug log
      if (response.ok && data.length > 0) {
        setPermissions(data[0]);
        console.log("Permissions set:", data[0]); // Debug log
      } else {
        setPermissions({ ...permissions, isPermission: 0 });
        setError("Failed to fetch permissions");
        setSnackbar({
          open: true,
          message: "Failed to fetch permissions",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching permissions:", error); // Debug log
      setError("Error fetching permissions");
      setSnackbar({
        open: true,
        message: "Error fetching permissions",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStreams = async () => {
  setStreamLoading(true);
  try {
    const response = await fetch(`${apiUrl}/Stream/GetAllStreams`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({}),
    });
    if (response.ok) {
      const data = await response.json();
      console.log("Streams Response:", data);
      // Ensure data is an array
      const streamsArray = Array.isArray(data) ? data : [];
      setStreams(streamsArray);
      setFilteredStreams(streamsArray);
    } else {
      setSnackbar({
        open: true,
        message: "Failed to fetch streams",
        severity: "error",
      });
    }
  } catch (error) {
    console.error("Error fetching streams:", error);
    setSnackbar({
      open: true,
      message: "Error fetching data",
      severity: "error",
    });
  } finally {
    setStreamLoading(false);
  }
};


  useEffect(() => {
    fetchFormId();
  }, []);

  useEffect(() => {
    if (formId) {
      fetchPermissions();
    }
  }, [formId]);

  useEffect(() => {
    if (permissions.isPermission && permissions.uRead) {
      fetchStreams();
    }
  }, [permissions]);

  useEffect(() => {
    const results = streams.filter((stream) =>
      stream.streamName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredStreams(results);
  }, [searchTerm, streams]);

  const handleClose = () => {
    setShowModal(false);
  };

  const handleAddNewStream = () => {
    navigate("/AddStream");
  };

  const handleEditStream = (streamName, streamID) => {
    navigate("/EditStream", { state: { streamName, streamID } });
  };

  const handleDeleteStream = async () => {
    try {
      const response = await fetch(
        `${apiUrl}/Stream/DeleteStream/${streamToDelete}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );

      if (response.ok) {
        setSnackbar({
          open: true,
          message: "Stream deleted successfully!",
          severity: "success",
        });
        fetchStreams();
      } else {
        setSnackbar({
          open: true,
          message: "Failed to delete stream",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error deleting stream:", error); // Debug log
      setSnackbar({
        open: true,
        message: "Error deleting stream: " + error.message,
        severity: "error",
      });
    }
    setShowModal(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "" });
  };

  if (loading) {
    return (
      <Container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error || !permissions.isPermission) {
    return (
      <Container>
        <Typography
          variant="h4"
          color="error"
          sx={{ mt: 4, textAlign: "center" }}
        >
          You are not authorised to view this page
        </Typography>
      </Container>
    );
  }

  const handleChangePage = (event, newPage) => {
  setPage(newPage);
};

const handleChangeRowsPerPage = (event) => {
  setRowsPerPage(parseInt(event.target.value, 10));
  setPage(1); // reset to page 1
};


const paginatedData = Array.isArray(filteredStreams)
  ? filteredStreams.slice(
      (page - 1) * rowsPerPage,
      page * rowsPerPage
    )
  : [];


  return (
    <Container>
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography
            variant="h4"
            component="div"
            style={{ fontWeight: "bold" }}
          >
            Stream
          </Typography>
        </Toolbar>
      </AppBar>
      <Paper
        elevation={3}
        style={{ padding: 16, width: "100%", margin: "auto", marginTop: 6 }}
      >
        <Box
          sx={{
            marginBottom: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <TextField
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: "300px" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
              style: { padding: "0px 8px" },
            }}
            sx={{
              width: 300,
              backgroundColor: "#f9f9f9",
            }}
          />
          <Tooltip
            title={
              permissions.uWrite ? "" : "You are not authorised to add streams"
            }
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                style={{
                  width: "160px",
                  height: "50px",
                  padding: "10px 0",
                  fontWeight: "bold",
                }}
                onClick={handleAddNewStream}
                disabled={!permissions.uWrite}
              >
                ADD NEW STREAM
              </Button>
            </span>
          </Tooltip>
        </Box>
        {permissions.uRead ? (
          <TableContainer component={Paper}>
            <Table
              sx={{
                minWidth: 650,
                border: "1px solid #ccc",
                "& .MuiTableCell-root": {
                  borderRight: "1px solid #ccc",
                  fontSize: "1rem",
                },
                "& .MuiTableHead-root .MuiTableCell-root": {
                  fontWeight: "bold",
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      borderRight: "1px solid #ccc",
                      borderBottom: "1px solid #ccc",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    Stream
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: "1px solid #ccc",
                      borderRight: "1px solid #ccc",
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {streamLoading ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredStreams.length > 0 ? (
                  paginatedData.map((stream, index) => (
                    <TableRow key={index} sx={{ borderTop: "1px solid #ccc" }}>
                      <TableCell
                        sx={{
                          borderRight: "1px solid #ccc",
                          fontSize: "1.1rem",
                        }}
                      >
                        {stream.streamName}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          borderRight: "1px solid #ccc",
                          fontSize: "1.1rem",
                        }}
                      >
                        <Tooltip
                          title={
                            permissions.uModify
                              ? ""
                              : "You are not authorised to edit streams"
                          }
                        >
                          <span>
                            <Button
                              variant="contained"
                              color="warning"
                              style={{ marginLeft: "10px" }}
                              onClick={() =>
                                handleEditStream(
                                  stream.streamName,
                                  stream.streamID
                                )
                              }
                              startIcon={<EditIcon />}
                              disabled={!permissions.uModify}
                            >
                              EDIT
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            permissions.uDelete
                              ? ""
                              : "You are not authorised to delete streams"
                          }
                        >
                          <span>
                            <Button
                              className="mx-2"
                              variant="contained"
                              color="error"
                              onClick={() => {
                                setStreamToDelete(stream.streamID);
                                setStreamNameToDelete(stream.streamName);
                                setShowModal(true);
                              }}
                              startIcon={<DeleteIcon />}
                              disabled={!permissions.uDelete}
                            >
                              DELETE
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      No Streams Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="h6" sx={{ textAlign: "center", mt: 2 }}>
            You are not authorised to view streams
          </Typography>
        )}

        {filteredStreams.length > 0 && (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
    <TextField
      select
      label="Rows per page"
      value={rowsPerPage}
      onChange={handleChangeRowsPerPage}
      sx={{ width: 150 }}
      SelectProps={{
        native: true,
      }}
    >
      {rowsPerPageOptions.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </TextField>

    <Box>
      <Typography variant="body2" sx={{ display: 'inline-block', marginRight: 2 }}>
        Page {page} of {Math.ceil(filteredStreams.length / rowsPerPage)}
      </Typography>
      <Button
        variant="outlined"
        onClick={() => handleChangePage(null, page - 1)}
        disabled={page === 1}
        sx={{ mr: 1 }}
      >
        Prev
      </Button>
      <Button
        variant="outlined"
        onClick={() => handleChangePage(null, page + 1)}
        disabled={page >= Math.ceil(filteredStreams.length / rowsPerPage)}
      >
        Next
      </Button>
    </Box>
  </Box>
)}

      </Paper>
      <Modal
        open={showModal}
        onClose={handleClose}
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <Box sx={{ backgroundColor: "#0B1F40", padding: "12px 16px" }}>
            <Typography
              id="delete-modal-title"
              variant="h6"
              sx={{ color: "#fff" }}
            >
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: "20px" }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete{" "}
              <strong>{streamNameToDelete}</strong> ?
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteStream}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default StreamPage;
